import { ComponentProps, FC } from 'react';
import * as React from 'react';
import { useAtomValue } from 'jotai';

import { cn, clampString } from '@/lib/utils';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { NavMain } from '@/components/nav-main';
import { WorkspaceSwitcher } from '@/components/workspace-switcher';
import { useSwitchBusiness } from '@/hooks/use-business-switch';
import { toast } from '@/hooks/use-toast';
import { trackEvent } from '@/helpers/external-tools-helper';
import { useTranslation } from 'react-i18next';
import { BusinessSwitchLoader } from '@/components/ui/business-switch-loader';

import { APP_ROUTE } from '@/constants';
import { useNavigate, useLocation } from 'react-router-dom';
import ActivePlanCard from '@/views/settings/billing/components/active-plan-card';
import { activationStateAtom } from '@/states';
import {
  ClientContainer,
  PagesContainer,
  TeamContainer,
  UserContainer,
} from '@/providers';
import { getSubscriptionName } from '@/views/settings/billing/utils';
import { useSession } from '@/states/common';
import ActivationFlowSidebar from '@/components/activation-flow-sidebar';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import { DemoSidebarCard } from '@/components/demo-sidebar-card';
import { useDemoMode } from '@/hooks/use-demo-mode';
import { businessSwitchAtom } from '@/states/business-switch';

// This is sample data.
const data = {
  teams: [
    {
      name: 'Acme Inc',
      logo: ({ className }: { className?: string }) => (
        <i className={cn('ti ti-buildings text-2xl', className)} />
      ),
      plan: 'Enterprise',
    },
    {
      name: 'Acme Corp.',
      logo: ({ className }: { className?: string }) => (
        <i className={cn('ti ti-buildings text-2xl', className)} />
      ),
      plan: 'Startup',
    },
  ],
  navMain: [
    {
      title: 'Dashboard',
      url: `/${APP_ROUTE.HOME}`,
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-layout-dashboard text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
    },
    {
      title: 'Connections',
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-plug-connected text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      pathMatch: `/${APP_ROUTE.CONNECTIONS}/*`,
      items: [
        {
          title: 'Data Sources',
          url: `/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}`,
        },
        {
          title: 'Destination',
          url: `/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DESTINATION}`,
        },
      ],
    },
    {
      title: 'Analytics',
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-chart-pie text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      url: `/${APP_ROUTE.ANALYTICS}`,
    },
    {
      title: 'Embedded Charts',
      url: `/${APP_ROUTE.EMBED}/${APP_ROUTE.CHARTS}`,
      dataTour: 'sidebar-embedded-charts',
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-code-circle text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
    },
    {
      title: 'Reporting',
      pathMatch: `/${APP_ROUTE.REPORTING}/*`,
      dataTour: 'sidebar-reporting',
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-report text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      items: [
        {
          title: 'Reports',
          url: `/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`,
          dataTour: 'sidebar-reports-item',
        },
        {
          title: 'Templates',
          url: `/${APP_ROUTE.REPORTING}/${APP_ROUTE.TEMPLATES}`,
        },
      ],
    },
    {
      title: 'Settings',
      pathMatch: `/${APP_ROUTE.SETTINGS}/*`,
      icon: ({ className }: { className?: string }) => (
        <i
          className={cn(
            'ti ti-settings-2 text-xl text-grayscale-600 font-normal',
            className
          )}
        />
      ),
      items: [
        {
          title: 'Profile',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.PROFILE}`,
        },
        {
          title: 'Account',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.ACCOUNT}`,
        },
        {
          title: 'Workspace',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.WORKSPACE}`,
        },
        {
          title: 'Team',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.TEAM}`,
        },
        {
          title: 'Billing',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}`,
        },
        {
          title: 'Connected apps',
          url: `/${APP_ROUTE.SETTINGS}/${APP_ROUTE.CONNECTED_APPS}`,
        },
      ],
    },
  ],
};

const BusinessSwitcher = () => {
  const { open } = useSidebar();
  const { state: userState } = UserContainer.useContainer();
  const navigate = useNavigate();
  const location = useLocation();
  const { state: clientState } = ClientContainer.useContainer();
  const switchBusiness = useSwitchBusiness();
  const { t } = useTranslation('workspace');
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = React.useState(
    false
  );
  const businessSwitchState = useAtomValue(businessSwitchAtom);
  const isSwitchingBusiness = businessSwitchState.isSwitching;
  const { data: session } = useSession();
  const { permission } = session;
  const userRole = permission?.role ?? 'member';

  const hasMultipleBusinesses =
    userState?.businessOptions && userState.businessOptions.length > 1;

  const handleBusinessSwitch = async (business: any) => {
    try {
      setIsBusinessDropdownOpen(false);

      await switchBusiness({
        business,
        currentPath: location.pathname,
        currentClientId: clientState?.selectedClient?.id,
      });

      trackEvent('navigation business switch', {
        fromBusinessId: userState?.business?.id,
        fromBusinessName: userState?.business?.name,
        toBusinessId: business.id,
        toBusinessName: business.name,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'switch-business-error',
          'Sorry, something went wrong. Please try again later.'
        ),
      });
    }
  };

  // Always show business switcher UI, even for single business
  const businessDropdownTrigger = (
    <button className="flex items-center gap-2.5 pl-2 pr-2 py-1.5 hover:bg-gray-50/80 transition-all rounded-md group bg-white">
      <div className="flex aspect-square size-7 items-center justify-center rounded-md bg-blue-50 ring-1 ring-blue-100/50 flex-shrink-0">
        {userState?.business?.logo ? (
          <img
            src={userState.business.logo}
            alt={userState.business.name}
            className="w-full h-full object-cover rounded-md"
          />
        ) : (
          <i className="ti ti-briefcase text-primary text-base" />
        )}
      </div>
      <span className="text-[13px] font-semibold text-gray-700 group-hover:text-gray-900 truncate max-w-[130px]">
        {clampString(userState?.business?.name, 20)}
      </span>
      <i className="ti ti-chevron-down text-gray-400 group-hover:text-gray-600 text-xs transition-all ml-auto" />
    </button>
  );

  if (!hasMultipleBusinesses) {
    return (
      <>
        {isSwitchingBusiness && (
          <BusinessSwitchLoader message={businessSwitchState.message} />
        )}
        <SidebarMenu>
          <SidebarMenuItem>
            {open ? (
              <div className="flex items-center justify-between h-[72px]">
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {businessDropdownTrigger}
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-xs">
                        {t(
                          'need-two-businesses-to-switch',
                          'You need at least two businesses to switch between them'
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <SidebarTrigger />
              </div>
            ) : (
              <div className="flex items-center justify-center w-full pt-2">
                <SidebarTrigger
                  className="bg-white shadow-sm"
                  iconClassName="text-grayscale-400"
                />
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </>
    );
  }

  return (
    <>
      {isSwitchingBusiness && (
        <BusinessSwitchLoader message={businessSwitchState.message} />
      )}
      <SidebarMenu>
        <SidebarMenuItem>
          {open ? (
            <div className="flex items-center justify-between h-[72px]">
              <DropdownMenu
                open={isBusinessDropdownOpen}
                onOpenChange={setIsBusinessDropdownOpen}
              >
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center gap-2.5 pl-2 pr-2 py-1.5 hover:bg-gray-50/80 transition-all rounded-md group bg-white">
                          <div className="flex aspect-square size-7 items-center justify-center rounded-md bg-blue-50 ring-1 ring-blue-100/50 flex-shrink-0">
                            {userState?.business?.logo ? (
                              <img
                                src={userState.business.logo}
                                alt={userState.business.name}
                                className="w-full h-full object-cover rounded-md"
                              />
                            ) : (
                              <i className="ti ti-briefcase text-primary text-base" />
                            )}
                          </div>
                          <span className="text-[13px] font-semibold text-gray-700 group-hover:text-gray-900 truncate max-w-[130px]">
                            {clampString(userState?.business?.name, 20)}
                          </span>
                          <i className="ti ti-chevron-down text-gray-400 group-hover:text-gray-600 text-xs transition-all ml-auto" />
                        </button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs font-semibold">
                          {userState?.business?.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {t(
                            'click-to-switch-business',
                            'Click to switch business'
                          )}
                        </span>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <DropdownMenuContent
                  className="w-[240px] rounded-xl font-sans p-1.5 shadow-lg border-gray-100"
                  align="start"
                  sideOffset={8}
                >
                  <div className="px-2.5 py-1.5 mb-0.5">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                      {t('businesses', 'Businesses')}
                    </span>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    {userState.businessOptions.map((business: any) => (
                      <DropdownMenuItem
                        key={business.id}
                        onClick={() => {
                          if (business.id !== userState?.business?.id) {
                            handleBusinessSwitch(business);
                          }
                        }}
                        disabled={business.id === userState?.business?.id}
                        className="rounded-lg cursor-pointer px-2 py-1.5 focus:bg-gray-50 data-[disabled]:opacity-60"
                      >
                        <div className="flex items-center gap-2.5 w-full">
                          <div className="flex aspect-square size-3 items-center justify-center rounded-md bg-blue-50 ring-1 ring-blue-100/50 flex-shrink-0">
                            {business.logo ? (
                              <img
                                src={business.logo}
                                alt={business.name}
                                className="w-full h-full object-cover rounded-md"
                              />
                            ) : (
                              <i className="ti ti-briefcase text-primary text-sm" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-[13px] font-semibold text-gray-900 truncate">
                                {business.name}
                              </span>
                              {business.isDemoAccount && (
                                <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-md font-medium">
                                  Demo
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] text-gray-500 font-medium">
                              {business.clients?.length || 0} workspace
                              {business.clients?.length !== 1 && 's'}
                            </span>
                          </div>
                          {userState?.business?.id === business.id && (
                            <i className="ti ti-check text-primary text-base flex-shrink-0" />
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </div>
                  {userRole !== 'member' && (
                    <>
                      <DropdownMenuSeparator className="my-1.5 bg-gray-100" />
                      <DropdownMenuItem
                        onClick={() => {
                          navigate(
                            `${APP_ROUTE.SETTINGS}/${APP_ROUTE.ACCOUNT}`
                          );
                          setIsBusinessDropdownOpen(false);
                        }}
                        className="rounded-lg cursor-pointer px-2 py-1.5 focus:bg-gray-50"
                      >
                        <i className="ti ti-settings text-gray-500 mr-2.5 text-sm" />
                        <span className="text-[13px] font-medium text-gray-700">
                          {t('manage_businesses', 'Manage Businesses')}
                        </span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <SidebarTrigger />
            </div>
          ) : (
            <div className="flex items-center justify-center w-full pt-2">
              <SidebarTrigger
                className="bg-white shadow-sm"
                iconClassName="text-grayscale-400"
              />
            </div>
          )}
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
};

const PlanProgress = () => {
  const { state: clientState } = ClientContainer.useContainer();
  const { data: session } = useSession();
  const { billing: billingInfo } = session;
  const navigate = useNavigate();
  const location = useLocation();

  // Use the unified plan limits hook
  const { limits, current, planName } = usePlanLimits();

  const planType = planName.toLowerCase();

  const paddleBillingInfo = session?.business?.billing;
  const isLegacyUser = paddleBillingInfo?.isLegacyUser;

  // Format display strings with infinity symbol for unlimited
  const datasourcesLength = `${current.datasources}/${
    limits.datasources === 'unlimited' ? '∞' : limits.datasources
  }`;
  const workspaceCount = `${current.workspaces}/${
    limits.workspaces === 'unlimited' ? '∞' : limits.workspaces
  }`;
  const userCount = `${current.users}/${
    limits.users === 'unlimited' ? '∞' : limits.users
  }`;

  if (isLegacyUser) {
    const planTypeForLegacyUser = getSubscriptionName(
      billingInfo?.status,
      billingInfo?.subscriptionName
    );

    return (
      <ActivePlanCard
        type={
          isLegacyUser
            ? (planTypeForLegacyUser?.plan?.toLowerCase() as
                | 'free'
                | 'starter'
                | 'professional'
                | 'team')
            : (planType as 'free' | 'starter' | 'professional' | 'team')
        }
        dataSource={datasourcesLength}
        workspace={workspaceCount}
        user={userCount}
        onUpgrade={() => {
          navigate(
            `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}`
          );
        }}
        showUpgradeButton={!location.pathname.includes('/billing')}
      />
    );
  }

  return (
    <ActivePlanCard
      type={planType as 'free' | 'starter' | 'professional' | 'team'}
      dataSource={datasourcesLength}
      workspace={workspaceCount}
      user={userCount}
      onUpgrade={() => {
        navigate(
          `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}`
        );
      }}
      showUpgradeButton={!location.pathname.includes('/billing')}
    />
  );
};

const SidebarFooterContent = () => {
  const { open, isMobile, isTablet } = useSidebar();
  const quickStart = useAtomValue(activationStateAtom);
  const { state: userState } = UserContainer.useContainer();
  const location = useLocation();
  const { isDemoMode } = useDemoMode();

  // Check if we're in the onboarding context to prevent API failures
  const isOnboarding = location.pathname.includes('onboarding');

  const completedSteps = Object.values(quickStart).filter(
    step => step === 'completed'
  ).length;
  const totalSteps = Object.keys(quickStart).length;
  const isActivationComplete = completedSteps === totalSteps;

  const shouldShowActivationFlow = userState.business?.activationFlow === false;

  // Always show footer content on mobile/tablet (expanded), on desktop only when open
  if (!open && !isMobile && !isTablet) return null;

  const renderContent = () => {
    if (isDemoMode) {
      return <DemoSidebarCard />;
    }

    if (shouldShowActivationFlow && !isActivationComplete && !isOnboarding) {
      return <ActivationFlowSidebar />;
    }

    if (!isOnboarding) {
      return <PlanProgress />;
    }

    return null;
  };

  return <div className="space-y-3">{renderContent()}</div>;
};

export const AppSidebar: FC<ComponentProps<typeof Sidebar>> = props => (
  <Sidebar collapsible="icon" {...props}>
    <SidebarHeader className="pt-0 gap-0">
      <BusinessSwitcher />
      <WorkspaceSwitcher workspaces={data.teams} />
    </SidebarHeader>

    <SidebarContent>
      <NavMain items={data.navMain} />
    </SidebarContent>

    <SidebarFooter>
      <SidebarFooterContent />
    </SidebarFooter>

    <SidebarRail />
  </Sidebar>
);
